import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './TopBar.css';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

interface TopBarProps {
  showSignOut?: boolean;
  onSignOut?: () => void;
  isDarkMode?: boolean;
  onToggleTheme?: () => void;
  onOpenProfileModal?: (category?: string) => void;
  onOpenAnalysisModal?: () => void;
  onAnalyzeProfile?: () => void | Promise<void>;
  showGetMyBagScoreAction?: boolean;
  showBagScoreAction?: boolean;
  profileMenuCounts?: Record<string, number>;
  hideMenu?: boolean;
}

const TopBar: React.FC<TopBarProps> = ({ showSignOut, onSignOut, isDarkMode, onToggleTheme, onOpenProfileModal, onOpenAnalysisModal, onAnalyzeProfile, showGetMyBagScoreAction, showBagScoreAction, profileMenuCounts, hideMenu = false }) => {
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const isLocalDevelopment = typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);

  useEffect(() => {
    if (isLocalDevelopment) {
      return;
    }

    // Inject Google tag once so Ads conversion and analytics both work.
    if (!document.getElementById('ga-gtag-script')) {
      const script1 = document.createElement('script');
      script1.id = 'ga-gtag-script';
      script1.async = true;
      script1.src = 'https://www.googletagmanager.com/gtag/js?id=AW-1048756556';
      document.head.appendChild(script1);

      const script2 = document.createElement('script');
      script2.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'AW-1048756556');
        gtag('config', 'G-5E61JKKBJ8');
      `;
      document.head.appendChild(script2);
    }
  }, [isLocalDevelopment]);

  // Google Analytics pageview tracking
  useEffect(() => {
    if (isLocalDevelopment) {
      return;
    }

    const w = window as any;
    if (typeof w !== 'undefined' && typeof w.gtag === 'function') {
      w.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [isLocalDevelopment, location]);

  useEffect(() => {
    if (menuOpen) {
      setProfileMenuOpen(true);
    }
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
        setProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [menuOpen]);

  const getFormattedUserName = () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user && user.first_name) {
          const name = user.first_name;
          return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
        }
      } catch (e) {
        // ignore JSON parse error
      }
    }
    return null;
  };

  const handleShare = async () => {
    const shareUrl = 'https://www.neutralfitgolf.com/';

    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({ title: 'NeutralFit', text: 'Check out NeutralFit Golf', url: shareUrl });
      } else {
        window.location.href = `mailto:neutralfitgolf@gmail.com?subject=NeutralFit%20Share&body=Check%20out%20NeutralFit:%20${encodeURIComponent(shareUrl)}`;
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Unable to share:', error);
        window.location.href = `mailto:neutralfitgolf@gmail.com?subject=NeutralFit%20Share&body=Check%20out%20NeutralFit:%20${encodeURIComponent(shareUrl)}`;
      }
    } finally {
      setMenuOpen(false);
    }
  };

  const handleFeedback = () => {
    if (!isLocalDevelopment && typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'menu_item_click', {
        menu_item: 'Give Feedback',
        menu_group: 'main_menu',
      });
    }
    window.location.href = 'mailto:neutralfitgolf@gmail.com?subject=NeutralFit Feedback';
    setMenuOpen(false);
  };

  const trackMenuClick = (label: string, group: string = 'main_menu') => {
    if (!isLocalDevelopment && typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'menu_item_click', {
        menu_item: label,
        menu_group: group,
      });
    }
  };

  const formattedName = getFormattedUserName();
  const showProfileMenu = location.pathname === '/club-fitting-mobile' || location.pathname === '/club-fitting-quiz';
  const isClubFittingPage = location.pathname === '/club-fitting';
  const isBasicFittingPage = location.pathname === '/basic-fitting';
  const isOnFittingPage = isClubFittingPage || showProfileMenu || isBasicFittingPage;
  const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const getFittedTarget = isOnFittingPage ? '/' : (isMobileDevice ? '/club-fitting-mobile' : '/club-fitting');

  return (
    <nav className="nf-navbar">
      <div className="nf-logo">
        <Link
          to={getFittedTarget}
          className="nf-logo-link"
          onClick={() => {
            trackMenuClick('Logo / Fitted Target', 'top_bar');
          }}
        >
          <span className="nf-logo-neutral">NEUTRAL</span><span className="nf-logo-fit">FIT</span>
        </Link>
      </div>
      <div className="nf-nav-links">
        {showSignOut && formattedName ? (
          <span className="nf-nav-user">Hi {formattedName}</span>
        ) : null}
        {!hideMenu && (
          <div className="mobile-menu-container" ref={menuRef}>
            <button
              className="mobile-menu-button"
              onClick={() => {
                const nextOpen = !menuOpen;
                setMenuOpen(nextOpen);
                if (nextOpen) {
                  trackMenuClick('Menu Open', 'main_menu');
                }
              }}
              aria-label="Open menu"
              style={{ fontSize: '1.35rem', padding: '0.45rem 0.6rem' }}
            >
              <span style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>⛳☰</span>
            </button>
            {menuOpen && (
              <div className="mobile-menu-dropdown">
                {showProfileMenu && (
                  <div>
                    <button
                      type="button"
                      className="mobile-menu-action mobile-menu-parent-action"
                      onClick={() => {
                        trackMenuClick('Player Profile', 'profile_menu');
                        setProfileMenuOpen(true);
                      }}
                      aria-expanded={profileMenuOpen}
                    >
                      <span className="mobile-menu-action-icon" aria-hidden="true">🏌️</span>
                      <span style={{ paddingRight: '30px' }}>Player Profile</span>
                      <span className="mobile-menu-caret">{profileMenuOpen ? '▾' : '▸'}</span>
                    </button>
                    {profileMenuOpen && (
                      <div className="mobile-menu-submenu">
                        {['General', 'Driver', 'Fairway Woods', 'Utilities', 'Irons', 'Wedges', 'Putter', 'Ball'].map(label => {
                          const count = label === 'General' ? 0 : profileMenuCounts?.[label] ?? 0;
                          const isGeneral = label === 'General';
                          return (
                            <button
                              key={label}
                              type="button"
                              className={`mobile-menu-subaction${count > 0 || isGeneral ? '' : ' mobile-menu-subaction--empty'}`}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                trackMenuClick(label, 'profile_submenu');
                                setMenuOpen(false);
                                setProfileMenuOpen(false);
                                onOpenProfileModal?.(label);
                              }}
                            >
                              <span className={`mobile-menu-subaction-label${count === 0 && !isGeneral ? ' mobile-menu-subaction-label--flagged' : ''}`}>{label}</span>
                              {count === 0 && !isGeneral ? (
                                <span className="mobile-menu-subaction-icon" aria-hidden="true">⚑</span>
                              ) : null}
                              {count > 0 && <span className="mobile-menu-subaction-count">{count}</span>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                {showGetMyBagScoreAction && onAnalyzeProfile && (
                  <button
                    type="button"
                    className="mobile-menu-action"
                    onClick={() => {
                      trackMenuClick('Get my Bag Score', 'main_menu');
                      onAnalyzeProfile();
                      setMenuOpen(false);
                      setProfileMenuOpen(false);
                    }}
                  >
                    <span className="mobile-menu-action-icon" aria-hidden="true">📊</span>
                    <span>Get my Bag Score</span>
                  </button>
                )}
                {showBagScoreAction && (
                  <button
                    type="button"
                    className="mobile-menu-action"
                    onClick={() => {
                      trackMenuClick('Bag Score', 'main_menu');
                      onOpenAnalysisModal?.();
                      setMenuOpen(false);
                      setProfileMenuOpen(false);
                    }}
                  >
                    <span className="mobile-menu-action-icon" aria-hidden="true">📊</span>
                    <span>Bag Score</span>
                  </button>
                )}
                {!isOnFittingPage && (
                  <Link
                    to={getFittedTarget}
                    className="mobile-menu-action"
                    onClick={() => {
                      trackMenuClick('Club Fitting', 'main_menu');
                      setMenuOpen(false);
                    }}
                  >
                    <span className="mobile-menu-action-icon" aria-hidden="true">🏌️</span>
                    <span>Club Fitting</span>
                  </Link>
                )}
                <div className="mobile-menu-language-row" style={{ display: 'none' }}>
                  <span className="mobile-menu-action-icon" aria-hidden="true">🌐</span>
                  <select
                    className="mobile-menu-language-select"
                    value={selectedLanguage}
                    onChange={(e) => {
                      setSelectedLanguage(e.target.value);
                      setMenuOpen(false);
                    }}
                  >
                    <option value="en">English</option>
                    <option value="es">Español</option>
                    <option value="zh">中文</option>
                    <option value="de">Deutsch</option>
                    <option value="ru">Русский</option>
                    <option value="ja">日本語</option>
                    <option value="fr">Français</option>
                    <option value="pt">Português</option>
                    <option value="it">Italiano</option>
                    <option value="ar">العربية</option>
                    <option value="hi">हिन्दी</option>
                    <option value="id">Indonesia</option>
                    <option value="ko">한국어</option>
                  </select>
                </div>
                
                {showSignOut ? (
                  <>
                    <button
                      type="button"
                      className="mobile-menu-action"
                      onClick={() => {
                        trackMenuClick('Sign Out', 'main_menu');
                        onSignOut?.();
                        setMenuOpen(false);
                      }}
                    >
                      <span className="mobile-menu-action-icon" aria-hidden="true">👋</span>
                      <span>Sign Out</span>
                    </button>
                  </>
                ) : (
                  <>
                    {location.pathname !== '/signup' && (
                      <Link
                        to="/signup"
                        onClick={() => {
                          trackMenuClick('Sign Up', 'main_menu');
                          setMenuOpen(false);
                        }}
                      >
                        <span className="mobile-menu-action-icon" aria-hidden="true">👤</span>
                        <span>Sign Up</span>
                      </Link>
                    )}
                    {location.pathname !== '/login' && (
                      <Link
                        to="/login"
                        onClick={() => {
                          trackMenuClick('Login', 'main_menu');
                          setMenuOpen(false);
                        }}
                      >
                        <span className="mobile-menu-action-icon" aria-hidden="true">🔑</span>
                        <span>Login</span>
                      </Link>
                    )}
                  </>
                )}
                <button
                  type="button"
                  className="mobile-menu-action"
                  onClick={() => {
                    trackMenuClick('Share NeutralFit', 'main_menu');
                    handleShare();
                  }}
                >
                  <span className="mobile-menu-action-icon" aria-hidden="true">🔗</span>
                  <span>Share NeutralFit</span>
                </button>
                <button
                  type="button"
                  className="mobile-menu-action"
                  onClick={() => {
                    trackMenuClick('Give Feedback', 'main_menu');
                    handleFeedback();
                  }}
                >
                  <span className="mobile-menu-action-icon" aria-hidden="true">💬</span>
                  <span>Give Feedback</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default TopBar;
